/* eslint react/display-name: 0 */
import React from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';

import { COLORS } from '_constants';

import { STATUS } from '../../../../server/constants';
import { getIsActiveAndNotExpiredLicensePool } from '_utils/licensePools';

import {
  Segment,
  Menu,
  Button,
  Grid,
  Dimmer,
  Loader
} from 'semantic-ui-react'

import Notes from '_views/notes'
import LineItem from '_views/lineItem'
import Usage from '_views/usage'
import Invoices from '_views/xero'

import InstitutionProfile from './profile'
import InstitutionLicensePools from './licensePools'
import InstitutionInvites from './invites'

import InstitutionAcsPage from '_views/activationCodes/institution'
import InstitutionKeys from '_views/keys'
import { OldLicensesDisplay } from "./oldLicensesDisplay";
import useCanAdminLicenses from '../../../hooks/useCanAdminLicenses';
import useIsAuthAdmin from '../../../hooks/useIsAuthAdmin';

const INSTITUTION_PANES = {
  ACCOUNT: 'Account',
  LICENSES: 'License Pools',
  INVOICES: 'Invoices',
  ACTIVATIONCODES: 'Activation Codes',
  KEYS: 'Keys',
  NOTES: 'Notes',
  INVITES: 'Invites'
}

const getProfile = R.pick([
  'status',
  'name',
  'xero_id',
  'capsule_id',
  'netsuite_id',
  'contact_email',
  'contact_name',
  'contact_phone',
  'description',
  'administrators',
  'oldlicense_count'
])

const hasInvoices = R.compose(
  x => x.length > 0,
  R.defaultTo([]),
  R.prop('invoices')
)

const getProfileFromObj = R.compose(
  getProfile,
  R.path(['props', 'institution'])
)

const getLicensePoolsFromObj = obj => obj && obj.props && obj.props.institution && obj.props.institution.license_pools ? obj.props.institution.license_pools : [];

const getPane = R.cond([
  [
    R.pathEq(['state', 'active'], INSTITUTION_PANES.ACCOUNT),
    obj => (<InstitutionProfile profile={getProfileFromObj(obj)} onProfileSubmit={obj.onProfileSubmit} />)
  ],
  [
    R.pathEq(['state', 'active'], INSTITUTION_PANES.LICENSES),
    obj => (
      <InstitutionLicensePools
        licensePools={ getLicensePoolsFromObj(obj) }
        onAddInstitutionLicensePool={ obj.onAddInstitutionLicensePool }
        onDeactivateInstitutionLicensePool={ obj.onDeactivateInstitutionLicensePool }
        onModifyInstitutionLicensePool={ obj.onModifyInstitutionLicensePool }
        onUpgradeInstitutionLicensePool={ obj.onUpgradeInstitutionLicensePool }
        onAddSublicenseToInstitutionLicensePool={ obj.onAddSublicenseToInstitutionLicensePool }
        onRemoveSublicenseFromInstitutionLicensePool={ obj.onRemoveSublicenseFromInstitutionLicensePool }
      />
    )
  ],
  [
    R.pathEq(['state', 'active'], INSTITUTION_PANES.INVOICES),
    obj => (<Invoices
      institution={R.path(['props', 'institution'], obj)}
      onRefreshList={R.path(['props', 'onRefreshList'], obj)}
      onRefresh={R.path(['props', 'onRefreshInstitution'], obj)}
      setNavigation={R.path(['props', 'acFuncs', 'setNavigation'], obj)}
    />)
  ],
  [
    R.pathEq(['state', 'active'], INSTITUTION_PANES.KEYS),
    obj => (<InstitutionKeys
      institution={R.path(['props', 'institution'], obj)}
      {...obj.props.keyFuncs}
    />)
  ],
  [
    R.pathEq(['state', 'active'], INSTITUTION_PANES.ACTIVATIONCODES),
    obj => (<InstitutionAcsPage
      {...obj.props.acFuncs}
      {...obj.props.acData}
      institution= { obj.props.institution }
      canTransferACLicenses={ true }
      canNavigateUsers={ true }
    />)
  ],
  [
    R.pathEq(['state', 'active'], INSTITUTION_PANES.NOTES),
    obj => (
      <Notes
        notes={R.pathOr([], ['props', 'institution', 'notes'], obj)}
        onAddNote={obj.onAddNote}
        onRemoveNote={obj.onRemoveNote}
      />
    )
  ],
  [
    R.pathEq(['state', 'active'], INSTITUTION_PANES.INVITES),
    obj => (<InstitutionInvites
      invites={R.pathOr([], ['props', 'institution', 'invites'], obj)}
      onAddInstitutionInvite={ obj.onAddInstitutionInvite }
      onRemoveInstitutionInvite={ obj.onRemoveInstitutionInvite }
    />)
  ],
  [
    R.T,
    (obj) => (<InstitutionProfile profile={R.path(['props', 'institution'], obj)} onProfileSubmit={obj.onProfileSubmit} />)
  ]
])

const confirmIt = (text, onDoIt) => e => {
  if (window.confirm(text)) {
    onDoIt()
  }
}

const InstitutionInfo = ({
  institution = {},
  onRemoveInstitution,
  onDeactivateInstitution
}) => {

  const canAdmin = useCanAdminLicenses();
  const canFullyDelete = useIsAuthAdmin();

  const onFullyDelete = () => {
    if (canFullyDelete && window.confirm('Remove this institution and all associated licenses ? WARNING: THIS IS NOT UNDOABLE!')) {
      onRemoveInstitution();
    } else if (!canFullyDelete) {
      window.alert('Please contact an Auth Admin if you wish to fully remove this institution and all its licenses');
    }
  };

  return (
    <React.Fragment>
      <LineItem label='ID' text={ institution._id } />
      <LineItem label='Name' text={ institution.name } />
      <OldLicensesDisplay count={ institution.oldlicense_count } />
      <br/>
      {
        (institution.license_pools || [])
          .filter(p => getIsActiveAndNotExpiredLicensePool(p))
          .map(p => <Usage key={ p._id } licensePool={ p } />)
      }
      {
        canAdmin && R.propEq('status', STATUS.ACTIVE, institution) ? (
          <Button
            negative
            content='deactivate'
            size='mini'
            onClick={confirmIt('Deactivate this institution?', onDeactivateInstitution)}
          />
        ) : null
      }
      {
        canAdmin && R.propEq('status', STATUS.NOT_ACTIVE, institution) ? (
          <Button
            negative
            content='remove'
            size='mini'
            onClick={ onFullyDelete }
          />
        ) : null
      }
    </React.Fragment>
  );
}

InstitutionInfo.propTypes = {
  institution: PropTypes.object.isRequired,
  onRemoveInstitution: PropTypes.func.isRequired,
  onDeactivateInstitution: PropTypes.func.isRequired
}

const InvoiceMenuItem = ({
  active,
  onMenuClick,
  hasInvoices
}) => hasInvoices
  ? <Menu.Item name={INSTITUTION_PANES.INVOICES} icon='list' active={active === INSTITUTION_PANES.INVOICES} onClick={onMenuClick} />
  : null

InvoiceMenuItem.propTypes = {
  active: PropTypes.string.isRequired,
  onMenuClick: PropTypes.func.isRequired,
  hasInvoices: PropTypes.bool.isRequired
}

const InstitutionMenu = ({
  active,
  hasInvoices,
  onMenuClick,
  onRefreshInstitution
}) => (
  <Menu attached='top' color={COLORS.INSTITUTIONS.MENU} inverted pointing borderless>
    <Menu.Menu position='left'>
      <Menu.Item name={INSTITUTION_PANES.ACCOUNT} icon='users' active={active === INSTITUTION_PANES.ACCOUNT} onClick={onMenuClick} />
      <Menu.Item name={INSTITUTION_PANES.LICENSES} icon='database' active={active === INSTITUTION_PANES.LICENSES} onClick={onMenuClick} />
      <InvoiceMenuItem hasInvoices={hasInvoices} onMenuClick={onMenuClick} active={active} />
      <Menu.Item name={INSTITUTION_PANES.ACTIVATIONCODES} icon='ticket' active={active === INSTITUTION_PANES.ACTIVATIONCODES} onClick={onMenuClick} />
      <Menu.Item name={INSTITUTION_PANES.KEYS} icon='key' active={active === INSTITUTION_PANES.KEYS} onClick={onMenuClick} />
      <Menu.Item name={INSTITUTION_PANES.NOTES} icon='comment' active={active === INSTITUTION_PANES.NOTES} onClick={onMenuClick} />
      <Menu.Item name={INSTITUTION_PANES.INVITES} icon='comment' active={active === INSTITUTION_PANES.INVITES} onClick={onMenuClick} />
    </Menu.Menu>

    <Menu.Menu position='right'>
      <Menu.Item icon='refresh' onClick={onRefreshInstitution} content='refresh'/>
    </Menu.Menu>
  </Menu>
)

InstitutionMenu.propTypes = {
  active: PropTypes.string.isRequired,
  hasInvoices: PropTypes.bool.isRequired,
  onMenuClick: PropTypes.func.isRequired,
  onRefreshInstitution: PropTypes.func.isRequired
}

// this class uses static class properties for auto-binding
// https://babeljs.io/docs/plugins/transform-class-properties/
// https://github.com/tc39/proposal-private-fields

class InstitutionItem extends React.PureComponent {
  state = {
    active: INSTITUTION_PANES.ACCOUNT
  }
  onMenuClick = (e, data) => {
    this.setState({active: data.name})
  }
  // institution actions
  onRefreshInstitution= () => this.props.onRefreshInstitution({
    institutionId: R.path(['institution', '_id'], this.props)
  })

  onRemoveInstitution= () => this.props.onRemoveInstitution({
    institutionId: R.path(['institution', '_id'], this.props),
    pageNum: R.prop('pageNum', this.props),
    perPage: R.prop('perPage', this.props)
  })

  onDeactivateInstitution = () => this.props.onDeactivateInstitution({
    institutionId: R.path(['institution', '_id'], this.props)
  })

  // license pool actions
  onAddInstitutionLicensePool = (payload) => this.props.onAddInstitutionLicensePool({
    institutionId: R.path(['institution', '_id'], this.props),
    payload
  })

  onModifyInstitutionLicensePool = (licensePoolId, payload) => this.props.onModifyInstitutionLicensePool({
    institutionId: R.path(['institution', '_id'], this.props),
    licensePoolId,
    payload
  })

  onUpgradeInstitutionLicensePool = (licensePoolId) => this.props.onUpgradeInstitutionLicensePool({
    institutionId: R.path(['institution', '_id'], this.props),
    licensePoolId
  })

  onDeactivateInstitutionLicensePool = (licensePoolId) => this.props.onDeactivateInstitutionLicensePool({
    institutionId: R.path(['institution', '_id'], this.props),
    licensePoolId,
  })

  onAddSublicenseToInstitutionLicensePool = (licensePoolId, name) => this.props.onAddSublicenseToInstitutionLicensePool({
    institutionId: R.path(['institution', '_id'], this.props),
    licensePoolId,
    payload: { name }
  })

  onRemoveSublicenseFromInstitutionLicensePool = (licensePoolId, sublicenseId) => this.props.onRemoveSublicenseFromInstitutionLicensePool({
    institutionId: R.path(['institution', '_id'], this.props),
    licensePoolId,
    sublicenseId
  })

  // profile actions
  onProfileSubmit = (payload) => this.props.onModifyOne({
    institutionId: R.path(['institution', '_id'], this.props),
    payload
  })

  // notes actions
  onAddNote = (note) => this.props.onAddNote({
    institutionId: R.path(['institution', '_id'], this.props),
    payload: {note}
  })

  onRemoveNote = (noteId) => this.props.onRemoveNote({
    institutionId: R.path(['institution', '_id'], this.props),
    noteId
  })

  // invite actions

  onAddInstitutionInvite = (payload) => this.props.onAddInstitutionInvite({
    institutionId: R.path(['institution', '_id'], this.props),
    payload
  })

  onRemoveInstitutionInvite = (inviteId) => this.props.onRemoveInstitutionInvite({
    institutionId: R.path(['institution', '_id'], this.props),
    inviteId
  })

  render () {
    return (
      <Segment
        as={Grid}
        basic
        tertiary={R.pathEq(['institution', 'status'], STATUS.NOT_ACTIVE, this.props)}
        divided
        stackable
        celled
      >
        <Grid.Row>
          <InstitutionMenu
            hasInvoices={hasInvoices(this.props.institution)}
            active={R.prop('active', this.state)}
            onMenuClick={this.onMenuClick}
            onRefreshInstitution={this.onRefreshInstitution}
          />
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width='4'>
            <InstitutionInfo
              institution={R.prop('institution', this.props)}
              onRemoveInstitution={this.onRemoveInstitution}
              onDeactivateInstitution={this.onDeactivateInstitution}
            />
          </Grid.Column>
          <Grid.Column width='12'>
            <Dimmer inverted active={this.props.isLoading === true}>
              <Loader />
            </Dimmer>
            { getPane(this) }
          </Grid.Column>
        </Grid.Row>
      </Segment>
    )
  }
}

InstitutionItem.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  institution: PropTypes.object.isRequired,
  pageNum: PropTypes.number.isRequired,
  perPage: PropTypes.number.isRequired,

  // inst. functions
  onRemoveInstitution: PropTypes.func.isRequired,
  onDeactivateInstitution: PropTypes.func.isRequired,
  onRefreshInstitution: PropTypes.func.isRequired,
  onRefreshList: PropTypes.func.isRequired,
  onModifyOne: PropTypes.func.isRequired,

  // lic pool functions
  onAddInstitutionLicensePool: PropTypes.func.isRequired,
  onDeactivateInstitutionLicensePool: PropTypes.func.isRequired,
  onModifyInstitutionLicensePool: PropTypes.func.isRequired,
  onUpgradeInstitutionLicensePool: PropTypes.func.isRequired,
  onAddSublicenseToInstitutionLicensePool: PropTypes.func.isRequired,
  onRemoveSublicenseFromInstitutionLicensePool: PropTypes.func.isRequired,

  // notes
  onAddNote: PropTypes.func.isRequired,
  onRemoveNote: PropTypes.func.isRequired,

  // invites
  onAddInstitutionInvite: PropTypes.func.isRequired,
  onRemoveInstitutionInvite: PropTypes.func.isRequired,

  // inst. key funcs
  keyFuncs: PropTypes.object.isRequired,

  // inst. Activation Code functions
  acFuncs: PropTypes.object.isRequired,
  acData: PropTypes.object.isRequired
}

export default InstitutionItem



// WEBPACK FOOTER //
// ./webAdmin/components/institutions/institution/index.js